import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowPathIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import useComments from '../../hooks/useComments.tsx';
import FilterBar from '../../components/Post/FilterBar.tsx';
import DetailedPane from '../../components/Comments/DetailedPane.tsx';
import DeleteConfirmation from '../../components/Comments/DeleteConfirmation.tsx';
import PaginationControls from '../../utils/pagination.tsx';
import CommentsTable from '../../components/Comments/CommentsTable.tsx';
import { Post } from '../../components/Post/post.ts';

interface DetailedCardProps {
  posts: Post[];
  activePost: any;
  currentSocial: any;
}

const DetailedCard: React.FC<DetailedCardProps> = ({
  posts,
  activePost,
  currentSocial,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postID } = useParams();
  const {
    comments,
    totalComments,
    selectedComment,
    currentPage,
    totalPages,
    sortOrder,
    statusFilter,
    hideReplies,
    isLoading,
    isDeleting,
    setComments,
    setSelectedComment,
    setCurrentPage,
    setSortOrder,
    setStatusFilter,
    setHideReplies,
    removeComment,
    loadComments,
    updateCommentHiddenStatus,
    replyToComment,
    searchText,
    setSearchText,
    removal,
    setRemoval,
  } = useComments({ postID });

  const [isDeleteConfirmationActive, setIsDeleteConfirmationActive] =
    useState(false);
  const [isFetchingComments, setIsFetchingComments] = useState(false);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const clickEvent = async (index: number) => {
    setSelectedComment(index);
  };

  const fetchLatestComments = async () => {
    setIsFetchingComments(true);
    setSelectedComment(null);
    setIsDeleteConfirmationActive(false);

    await loadComments();
    setIsFetchingComments(false);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (currentPage === 1) {
      queryParams.delete('page');
    } else {
      queryParams.set('page', currentPage.toString());
    }
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true,
    });
  }, [currentPage, navigate, location.pathname, location.search]);

  return (
    <>
      {/* Main container that fills remaining space without overflow */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Content container with proper flex layout */}
        <div className="bg-grey flex divide-x divide-white divide-opacity-20 flex-1">
          {/* Left column - Comments list and filters */}
          <div
            className={`flex flex-col w-9/12 ${
              isDeleteConfirmationActive && 'pointer-events-none'
            }`}
          >
            {/* Filter bar at the top */}
            <FilterBar
              sortOrder={sortOrder}
              setSortOrder={(value: any) => {
                setSortOrder(value);
                setCurrentPage(1);
              }}
              statusFilter={statusFilter}
              setStatusFilter={(value: any) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              hideReplies={hideReplies}
              setHideReplies={(value: any) => {
                setHideReplies(value);
                setCurrentPage(1);
              }}
              searchText={searchText}
              setSearchText={setSearchText}
              removal={removal}
              setRemoval={setRemoval}
              autoMod={false}
              setAutoMod={undefined}
            />

            {/* Comments table - fills remaining space without creating scrollable area */}
            <div className="flex-1 min-h-0">
              <CommentsTable
                comments={comments}
                selectedComment={selectedComment}
                setSelectedComment={setSelectedComment}
                isLoading={isLoading}
                totalComments={totalComments}
                clickEvent={clickEvent}
              />
            </div>

            {/* Pagination at the bottom */}
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          </div>

          {/* Right column - Detailed view */}
          <div className={`relative w-5/12 min-w-96 flex flex-col`}>
            <div
              className={`${
                isDeleteConfirmationActive && 'blur-sm pointer-events-none'
              }`}
            >
              <DetailedPane
                currentSocial={currentSocial}
                comments={comments}
                setComments={setComments}
                selectedComment={selectedComment}
                setSelectedComment={setSelectedComment}
                deleteWindow={() => setIsDeleteConfirmationActive(true)}
                isLoading={isLoading}
                updateCommentHiddenStatus={updateCommentHiddenStatus}
                replyToComment={replyToComment}
              />
            </div>

            {/* Delete confirmation modal */}
            {isDeleteConfirmationActive && (
              <DeleteConfirmation
                cancel={setIsDeleteConfirmationActive}
                removeComment={removeComment}
                isDeleting={isDeleting}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailedCard;
